// src/components/custom/CustomLandingView.jsx
//
// An org-defined LANDING PAGE (utils/customNav.js, kind: 'landing'). Where a
// custom topic is a list of modules, this is a single designed page: a hero, a
// statement with its commitments, the steps to take part, and actions that
// either open another tab in the app or link out.
//
// Entirely data-driven. Content is the `landing` map on the org's
//   organizations/{orgId}/private_topics/{sectionId}
// doc, so it is private to the org under the existing rules and nothing here
// names a client. Hockey Alberta's Player Pledge is the first page built on it.
// Any `modules` on the same doc (loaded through Content Library) render below
// as resources, so a client can attach its videos and PDFs without new code.

import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ArrowRight, ExternalLink, Play, FileText, Headphones, X, Loader2, Quote } from 'lucide-react';
import { db } from '../../config/firebase-config';
import { useOrganization } from '../../context/OrganizationContext';
import { heroClass, heroStyle, heroEyebrowStyle } from '../../utils/orgTheme';
import CustomYouTubePlayer from '../../shared/CustomYouTubePlayer';

const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
    return (match && match[2].length === 11) ? match[2] : null;
};

const isExternal = (target) => /^(https?:\/\/|\/)/i.test(target || '');
const TYPE_ICON = { Video: Play, PDF: FileText, Audio: Headphones, Link: ExternalLink };

const CustomLandingView = ({ section, onNavigate }) => {
    const { orgData } = useOrganization();
    const orgId = orgData?.id;
    const [page, setPage] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [activeVideo, setActiveVideo] = useState(null);

    useEffect(() => {
        if (!orgId || !section?.id) return undefined;
        setLoaded(false);
        const unsub = onSnapshot(
            doc(db, 'organizations', orgId, 'private_topics', section.id),
            (snap) => { setPage(snap.exists() ? snap.data() : null); setLoaded(true); },
            () => { setPage(null); setLoaded(true); }
        );
        return () => unsub();
    }, [orgId, section?.id]);

    if (!loaded) {
        return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-brand-primary" size={32} /></div>;
    }

    const landing = page?.landing || {};
    const headline = landing.headline || page?.title || section.label;
    const commitments = Array.isArray(landing.commitments) ? landing.commitments : [];
    const steps = Array.isArray(landing.steps) ? landing.steps : [];
    const actions = Array.isArray(landing.actions) ? landing.actions : [];
    const resources = (Array.isArray(page?.modules) ? page.modules : []).flatMap((m) => m.resources || []);

    const runAction = (target) => {
        if (!target) return;
        if (isExternal(target)) window.open(target, '_blank', 'noopener,noreferrer');
        else if (onNavigate) onNavigate(target);
    };

    const openResource = (r) => {
        if (r.type === 'Video' && getYouTubeVideoId(r.url)) setActiveVideo(r);
        else if (r.url) window.open(r.url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in pb-20">

            {activeVideo && (
                <div className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setActiveVideo(null)}>
                    <div className="bg-white w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-slate-800 truncate">{activeVideo.title}</h3>
                            <button type="button" onClick={() => setActiveVideo(null)} className="p-2 hover:bg-slate-200 rounded-full" aria-label="Close"><X size={20} className="text-slate-500" /></button>
                        </div>
                        <div className="w-full aspect-video bg-black">
                            <CustomYouTubePlayer videoId={getYouTubeVideoId(activeVideo.url)} />
                        </div>
                    </div>
                </div>
            )}

            {/* HERO */}
            <div className={`${heroClass(orgData)} rounded-3xl p-6 md:p-10 text-white relative overflow-hidden shadow-sm`} style={heroStyle(orgData)}>
                <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
                <div className="relative z-10 max-w-2xl">
                    {landing.eyebrow && (
                        <p className="text-[11px] font-black uppercase tracking-widest text-white/70 mb-2" style={heroEyebrowStyle(orgData)}>{landing.eyebrow}</p>
                    )}
                    <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">{headline}</h1>
                    {landing.tagline && <p className="text-lg md:text-xl font-bold text-white/80 mt-2">{landing.tagline}</p>}
                    {landing.intro && <p className="text-white/80 font-medium leading-relaxed mt-4">{landing.intro}</p>}
                    {actions.length > 0 && (
                        <div className="flex flex-wrap gap-3 mt-6">
                            {actions.map((a, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => runAction(a.target)}
                                    className={`px-5 py-3 rounded-full font-black text-sm flex items-center gap-2 transition-colors ${
                                        a.primary ? 'bg-white text-brand-primary hover:bg-slate-50 shadow-lg' : 'bg-white/10 hover:bg-white/20 text-white border border-white/30'
                                    }`}
                                >
                                    {a.label}
                                    {isExternal(a.target) ? <ExternalLink size={15} /> : <ArrowRight size={15} />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* STATEMENT + COMMITMENTS - the highlight surface and accent bar come
                from the org theme (--brand-highlight / --brand-accent). */}
            {(landing.statement || commitments.length > 0) && (
                <div className="rounded-3xl border border-slate-200 p-6 md:p-8" style={{ backgroundColor: 'var(--brand-highlight)', borderLeft: '6px solid var(--brand-accent)' }}>
                    {landing.statement && (
                        <p className="text-lg md:text-xl font-black text-slate-900 leading-snug flex items-start gap-3">
                            <Quote size={22} className="text-brand-primary shrink-0 mt-1" />
                            <span>{landing.statement}</span>
                        </p>
                    )}
                    {commitments.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                            {commitments.map((c, i) => (
                                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5">
                                    {c.keyword && <p className="text-xl font-black text-brand-primary tracking-tight">{c.keyword}</p>}
                                    <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1">{c.text}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* STEPS */}
            {steps.length > 0 && (
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                    <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4">{landing.stepsTitle || 'How to take part'}</h2>
                    <div className="space-y-2">
                        {steps.map((s, i) => (
                            <div key={i} className="flex gap-3 items-start px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100">
                                <div className="w-7 h-7 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center font-black shrink-0 text-xs mt-0.5">{i + 1}</div>
                                <div>
                                    <h3 className="font-bold text-slate-800 text-sm leading-tight">{s.title}</h3>
                                    {s.text && <p className="text-sm text-slate-500 leading-relaxed mt-0.5">{s.text}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* RESOURCES - anything loaded onto this page through Content Library */}
            {resources.length > 0 && (
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                    <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4">Resources</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {resources.map((r) => {
                            const Icon = TYPE_ICON[r.type] || ExternalLink;
                            return (
                                <button key={r.id} type="button" onClick={() => openResource(r)} className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-300 hover:bg-white transition-colors text-left">
                                    <span className="w-8 h-8 rounded-lg bg-brand-light text-brand-primary flex items-center justify-center shrink-0 mt-0.5"><Icon size={15} /></span>
                                    <span className="min-w-0">
                                        <span className="block text-sm font-bold text-slate-800 leading-snug">{r.title}</span>
                                        {r.content && <span className="block text-xs text-slate-500 font-medium mt-0.5 line-clamp-2">{r.content}</span>}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Nothing authored yet: say so plainly rather than show a bare hero. */}
            {!landing.statement && commitments.length === 0 && steps.length === 0 && resources.length === 0 && (
                <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-10 text-center">
                    <p className="text-sm text-slate-500 font-medium">{page?.description || 'This page is being prepared. Check back soon.'}</p>
                </div>
            )}
        </div>
    );
};

export default CustomLandingView;
